import { useState, useCallback } from "react";

interface UseIdentityPreviewReturn {
  identityPreviewOpen: boolean;
  identityPreviewContent: string | null;
  identityPreviewType: "image" | "pdf" | null;
  identityPreviewFileName: string;
  openIdentityPreview: (
    content: string,
    fileName: string,
    isPdf: boolean
  ) => Promise<void>;
  closeIdentityPreview: () => void;
}

export const useIdentityPreview = (): UseIdentityPreviewReturn => {
  const [identityPreviewOpen, setIdentityPreviewOpen] = useState(false);
  const [identityPreviewContent, setIdentityPreviewContent] = useState<
    string | null
  >(null);
  const [identityPreviewType, setIdentityPreviewType] = useState<
    "image" | "pdf" | null
  >(null);
  const [identityPreviewFileName, setIdentityPreviewFileName] =
    useState<string>("");

  const openIdentityPreview = useCallback(
    async (content: string, fileName: string, isPdf: boolean) => {
      // For external URLs (http/https), open in new tab instead of modal
      // This avoids CORS/X-Frame-Options issues
      if (content.startsWith("http://") || content.startsWith("https://")) {
        window.open(content, "_blank");
        return;
      }

      setIdentityPreviewFileName(fileName);

      // Determine actual content type from data URL if available
      let actualType: "image" | "pdf" = isPdf ? "pdf" : "image";
      let mimeTypeFromData: string | null = null;
      let processedContent = content;

      if (content.startsWith("data:")) {
        const mimeMatch = content.match(/^data:([^;,]+)/);
        if (mimeMatch) {
          mimeTypeFromData = mimeMatch[1].toLowerCase();
          if (mimeTypeFromData === "application/pdf") {
            actualType = "pdf";
          } else if (mimeTypeFromData.startsWith("image/")) {
            actualType = "image";
          }
        }
      }

      // Check for mismatch: filename says PDF but data says image
      // This can happen when PDFs are incorrectly stored with image MIME types
      const fileNameIsPdf = fileName.toLowerCase().endsWith(".pdf");
      const dataIsImage = mimeTypeFromData?.startsWith("image/") ?? false;

      if (fileNameIsPdf && dataIsImage && content.startsWith("data:")) {
        processedContent = content.replace(
          /^data:image\/[^;,]+/,
          "data:application/pdf"
        );
        actualType = "pdf";
      }

      setIdentityPreviewType(actualType);
      setIdentityPreviewOpen(true);

      // For blob URLs, we need to fetch and convert to data URL
      if (content.startsWith("blob:")) {
        try {
          const response = await fetch(content);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setIdentityPreviewContent(reader.result as string);
          };
          reader.readAsDataURL(blob);
        } catch (error) {
          console.error("Failed to load document:", error);
          setIdentityPreviewContent(content);
        }
      } else {
        setIdentityPreviewContent(processedContent);
      }
    },
    []
  );

  const closeIdentityPreview = useCallback(() => {
    setIdentityPreviewOpen(false);
    // Small delay to allow modal close animation before clearing content
    setTimeout(() => {
      setIdentityPreviewContent(null);
      setIdentityPreviewType(null);
      setIdentityPreviewFileName("");
    }, 200);
  }, []);

  return {
    identityPreviewOpen,
    identityPreviewContent,
    identityPreviewType,
    identityPreviewFileName,
    openIdentityPreview,
    closeIdentityPreview,
  };
};
